import { useCallback } from 'react'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { IRatingColumnSettings } from '@/core/models/columns/rating'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompFieldRatingProps } from './comp'

export interface CompFieldRatingWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: CompFieldRatingProps) => React.ReactNode
}

/**
 * CompFieldRatingWrapperCore
 */
export function CompFieldRatingWrapperCore({
  infos: { settings, containerClient, detailTableId, detailRowIndex, component },
  form,
  children,
}: CompFieldRatingWrapperProps) {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { formState, setFormState, dataSourceTable } = form

  // ========================= data =========================
  const columnId = settings[ComponentSettingKey.FieldRatingDataValue] as string
  const column = dataSourceTable?.columnsMap.get(columnId)
  const columnSettings = column?._Settings as unknown as IRatingColumnSettings | undefined

  const labelData = settings[ComponentSettingKey.FieldRatingDataLabel]
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)

  const defaultValue = useParseVariableSelect(
    settings[ComponentSettingKey.FieldRatingDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const isEditable = getSafely(settings, [ComponentSettingKey.FieldRatingDataDefaultValueEditable], true)

  const minRating = columnSettings?.ratingMin ?? 0

  // ========================= design =========================

  // ========================= behavior =========================
  const required = Boolean(settings[ComponentSettingKey.FieldRatingBehaviorRequired])

  // ========================= form state =========================
  const value = columnId && typeof formState[columnId] !== 'undefined' ? formState[columnId] : defaultValue

  const handleChange = useCallback(
    (value: number) => {
      let newValue = value

      if (columnSettings && newValue < columnSettings.ratingMin) {
        newValue = columnSettings.ratingMin
      }

      setFormState?.((prev) => ({
        ...prev,
        [columnId]: newValue,
      }))
    },
    [columnId, columnSettings, setFormState]
  )

  // ========================= render =========================

  const ratingProps: CompFieldRatingProps = {
    label,
    disabled: !isEditable,
    required,
    count: columnSettings?.ratingMax,
    unit: columnSettings?.ratingIncrement as 'full' | 'half' | 'float',
    value: isNaN(Number(value)) ? minRating : Number(value),
    onChange: handleChange,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(ratingProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(ratingProps)
}
